import { X, CheckCircle, XCircle, Calendar, AlertCircle, PackageX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Notification, NotificationType } from "@/types/notifications";

interface NotificationBannerProps {
  notification: Notification;
  onDismiss: (id: string) => void;
  onMarkAsRead: (id: string) => void;
}

const ICON_MAP: Record<NotificationType, React.ElementType> = {
  pickup_approved: CheckCircle,
  pickup_denied: XCircle,
  pickup_reschedule_requested: Calendar,
  package_denied: PackageX,
  pickup_auto_canceled: AlertCircle,
};

const VARIANT_STYLES: Record<NotificationType, {
  container: string;
  icon: string;
  title: string;
}> = {
  pickup_approved: {
    container: "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800",
    icon: "text-green-600 dark:text-green-400",
    title: "text-green-800 dark:text-green-200",
  },
  pickup_denied: {
    container: "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800",
    icon: "text-red-600 dark:text-red-400",
    title: "text-red-800 dark:text-red-200",
  },
  pickup_reschedule_requested: {
    container: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800",
    icon: "text-amber-600 dark:text-amber-400",
    title: "text-amber-800 dark:text-amber-200",
  },
  package_denied: {
    container: "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800",
    icon: "text-red-600 dark:text-red-400",
    title: "text-red-800 dark:text-red-200",
  },
  pickup_auto_canceled: {
    container: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800",
    icon: "text-amber-600 dark:text-amber-400",
    title: "text-amber-800 dark:text-amber-200",
  },
};

export const NotificationBanner = ({
  notification,
  onDismiss,
  onMarkAsRead,
}: NotificationBannerProps) => {
  const Icon = ICON_MAP[notification.type as NotificationType] || AlertCircle;
  const styles = VARIANT_STYLES[notification.type as NotificationType] || VARIANT_STYLES.pickup_auto_canceled;
  const isUnread = !notification.read_at;

  const handleClick = () => {
    if (isUnread) {
      onMarkAsRead(notification.id);
    }
  };

  return (
    <div
      className={cn(
        "relative flex items-start gap-3 p-4 rounded-lg border transition-all",
        styles.container,
        isUnread && "ring-2 ring-primary/20"
      )}
      onClick={handleClick}
      role="button"
      tabIndex={0}
    >
      {/* Unread indicator */}
      {isUnread && (
        <div className="absolute top-3 left-3 w-2 h-2 rounded-full bg-primary animate-pulse" />
      )}

      <div className={cn("flex-shrink-0 mt-0.5", isUnread && "ml-3")}>
        <Icon className={cn("w-5 h-5", styles.icon)} />
      </div>

      <div className="flex-1 min-w-0">
        <p className={cn("font-medium text-sm", styles.title)}>
          {notification.title}
        </p>
        <p className="text-sm text-muted-foreground mt-0.5">
          {notification.message}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {new Date(notification.created_at).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })}
        </p>
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="flex-shrink-0 h-8 w-8 rounded-full opacity-70 hover:opacity-100"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(notification.id);
        }}
      >
        <X className="w-4 h-4" />
        <span className="sr-only">Dismiss</span>
      </Button>
    </div>
  );
};
